import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import axios from 'axios';
import CommonsTree, { CommonsTreeBranch } from '../../../commons/CommonsTree';
import {
  SysPerfilSysTaskPersistRow,
  SysPerfilSysTaskRow,
  fetchPerfilTaskAssignments,
  isAllowTrue,
  junctionRowsBySysTaskId,
  putPerfilTaskAssignments,
} from '../../../manager/sys-perfil-sys-task.api';

interface SysTaskCatalogItem {
  id: number;
  task: string;
  modulo: string;
}

/** Asignación editable: nuevas filas llevan `id: null` hasta el PUT de guardado. */
type LocalTaskAssignment = {
  id: number | null;
  allow: boolean;
  sysPerfil: Record<string, unknown>;
  sysTask: Record<string, unknown>;
  junctionMeta: Record<string, unknown>;
};

export interface PerfilSistemaTasksPanelHandle {
  /** PUT `api/sys-perfils/:id/tasks` con el mismo esquema que GET. */
  saveAssignments: () => Promise<void>;
  /** Recarga catálogo y asignaciones desde el backend. Lanza si falla. */
  reload: () => Promise<void>;
}

interface PerfilSistemaTasksPanelProps {
  perfilId: string;
  accentColor?: string;
  /** Deshabilitar checkboxes durante guardado general de la página */
  disabled?: boolean;
}

function coerceJunctionId(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Campos raíz del vínculo salvo id / relaciones / allow (p. ej. auditoría del GET). */
function junctionMetaFromRow(j: Record<string, unknown>): Record<string, unknown> {
  const skip = new Set(['id', 'sysPerfil', 'sysTask', 'allow']);
  const meta: Record<string, unknown> = {};
  Object.keys(j).forEach(k => {
    if (!skip.has(k)) meta[k] = j[k];
  });
  return meta;
}

function sortCatalog(items: SysTaskCatalogItem[]) {
  return [...items].sort((a, b) => {
    const m = String(a.modulo ?? '').localeCompare(String(b.modulo ?? ''), 'es');
    if (m !== 0) return m;
    return String(a.task ?? '').localeCompare(String(b.task ?? ''), 'es');
  });
}

function persistSysPerfil(
  row: LocalTaskAssignment | undefined,
  perfilIdNum: number,
): SysPerfilSysTaskPersistRow['sysPerfil'] {
  const p = row?.sysPerfil;
  if (!p || typeof p !== 'object') {
    return { id: perfilIdNum };
  }
  return {
    ...p,
    id: coerceJunctionId(p.id) ?? perfilIdNum,
  } as SysPerfilSysTaskPersistRow['sysPerfil'];
}

function persistSysTask(
  row: LocalTaskAssignment | undefined,
  task: SysTaskCatalogItem,
): SysPerfilSysTaskPersistRow['sysTask'] {
  const s = row?.sysTask;
  if (!s || typeof s !== 'object') {
    return { id: task.id, task: task.task, modulo: task.modulo };
  }
  const taskId = coerceJunctionId(s.id) ?? task.id;
  return {
    ...s,
    id: taskId,
    task: typeof s.task === 'string' ? s.task : task.task,
    modulo: typeof s.modulo === 'string' ? s.modulo : task.modulo,
  };
}

function patchTaskAllow(
  prev: Record<number, LocalTaskAssignment>,
  task: SysTaskCatalogItem,
  next: boolean,
  perfilPid: number,
): Record<number, LocalTaskAssignment> {
  const cur = prev[task.id];
  if (next) {
    return {
      ...prev,
      [task.id]: cur
        ? { ...cur, allow: true }
        : {
            id: null,
            allow: true,
            junctionMeta: {},
            sysPerfil: { id: perfilPid },
            sysTask: { id: task.id, task: task.task, modulo: task.modulo },
          },
    };
  }
  if (!cur) return prev;
  if (cur.id == null) {
    const copy = { ...prev };
    delete copy[task.id];
    return copy;
  }
  return { ...prev, [task.id]: { ...cur, allow: false } };
}

function buildPersistRows(
  catalog: SysTaskCatalogItem[],
  localByTaskId: Record<number, LocalTaskAssignment>,
  perfilIdNum: number,
): SysPerfilSysTaskPersistRow[] {
  const rows: SysPerfilSysTaskPersistRow[] = [];
  for (const task of catalog) {
    const row = localByTaskId[task.id];
    const allow = row ? isAllowTrue(row.allow) : false;
    const id = row != null ? coerceJunctionId(row.id) : null;
    if (!allow && id == null) continue;

    rows.push({
      ...(row?.junctionMeta ?? {}),
      id,
      allow,
      sysPerfil: persistSysPerfil(row, perfilIdNum),
      sysTask: persistSysTask(row, task),
    });
  }
  return rows;
}

const PerfilSistemaTasksPanel = forwardRef<PerfilSistemaTasksPanelHandle, PerfilSistemaTasksPanelProps>(
  function PerfilSistemaTasksPanel({ perfilId, accentColor = '#6366f1', disabled = false }, ref) {
    const pid = Number(perfilId);

    const [catalog, setCatalog] = useState<SysTaskCatalogItem[]>([]);
    const [localByTaskId, setLocalByTaskId] = useState<Record<number, LocalTaskAssignment>>({});
    /** `allow` efectivo tras última carga del backend (para resaltar cambios a true). */
    const [initialAllowByTaskId, setInitialAllowByTaskId] = useState<Record<number, boolean>>({});

    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState<string | null>(null);

    const reload = useCallback(async (opts?: { throwOnError?: boolean }) => {
      setLoading(true);
      setFetchError(null);
      try {
        const [stRes, assigns] = await Promise.all([
          axios.get<SysTaskCatalogItem[]>(`api/sys-tasks?page=0&size=999&sort=modulo,asc&sort=id,asc`),
          fetchPerfilTaskAssignments(perfilId),
        ]);

        const list = Array.isArray(stRes.data) ? stRes.data : [];
        const sorted = sortCatalog(list);
        setCatalog(sorted);

        const assignsList = Array.isArray(assigns) ? assigns : [];
        /** El GET ya está acotado a este perfil; no filtrar por sysPerfil.id (evita perder vínculos y mandar id null). */
        const merged = junctionRowsBySysTaskId(assignsList as SysPerfilSysTaskRow[]);
        const init: Record<number, LocalTaskAssignment> = {};
        for (const t of sorted) {
          const j = merged[t.id];
          if (j) {
            const jr = j as unknown as Record<string, unknown>;
            init[t.id] = {
              id: coerceJunctionId(jr.id),
              allow: isAllowTrue(j.allow),
              sysPerfil: j.sysPerfil && typeof j.sysPerfil === 'object' ? { ...(j.sysPerfil as object) } : {},
              sysTask:
                j.sysTask && typeof j.sysTask === 'object'
                  ? { ...(j.sysTask as object) }
                  : ({ id: t.id } as Record<string, unknown>),
              junctionMeta: junctionMetaFromRow(jr),
            };
          }
        }
        const initialSel: Record<number, boolean> = {};
        for (const t of sorted) {
          const j = merged[t.id];
          initialSel[t.id] = j ? isAllowTrue(j.allow) : false;
        }
        setInitialAllowByTaskId(initialSel);
        setLocalByTaskId(init);
      } catch (err) {
        setFetchError('No se pudieron cargar las tareas o los permisos del perfil.');
        setCatalog([]);
        setLocalByTaskId({});
        setInitialAllowByTaskId({});
        if (opts?.throwOnError) {
          throw err instanceof Error ? err : new Error('Error al cargar permisos desde el servidor.');
        }
      } finally {
        setLoading(false);
      }
    }, [perfilId]);

    useEffect(() => {
      if (!Number.isFinite(pid)) return;
      void reload();
    }, [reload, pid]);

    const applyLocalAllow = useCallback((task: SysTaskCatalogItem, next: boolean) => {
      setLocalByTaskId(prev => patchTaskAllow(prev, task, next, pid));
    }, [pid]);

    const applyModuleAllows = useCallback((moduloTasks: SysTaskCatalogItem[], next: boolean) => {
      setLocalByTaskId(prev => {
        let acc = prev;
        for (const task of moduloTasks) {
          acc = patchTaskAllow(acc, task, next, pid);
        }
        return acc;
      });
    }, [pid]);

    useImperativeHandle(
      ref,
      () => ({
        saveAssignments: async () => {
          if (loading) {
            throw new Error('Aún se están cargando los permisos.');
          }
          const body = buildPersistRows(catalog, localByTaskId, pid);
          await putPerfilTaskAssignments(perfilId, body);
          await reload();
        },
        reload: () => reload({ throwOnError: true }),
      }),
      [loading, catalog, localByTaskId, pid, perfilId, reload],
    );

    const branches: CommonsTreeBranch[] = useMemo(() => {
      const byModule = new Map<string, SysTaskCatalogItem[]>();
      for (const t of catalog) {
        const m = t.modulo?.trim() || '— Sin módulo —';
        if (!byModule.has(m)) byModule.set(m, []);
        byModule.get(m)!.push(t);
      }
      const modKeys = Array.from(byModule.keys()).sort((a, b) => a.localeCompare(b, 'es'));

      return modKeys.map(modulo => {
        const tasksInMod = byModule.get(modulo) ?? [];
        const leafChecked = (t: SysTaskCatalogItem) => {
          const r = localByTaskId[t.id];
          return r ? isAllowTrue(r.allow) : false;
        };
        const anyLeafChecked = tasksInMod.some(leafChecked);
        const total = tasksInMod.length;
        const selected = tasksInMod.filter(leafChecked).length;
        const allSelectedInMod = total > 0 && selected === total;

        return {
          key: modulo,
          title: `${modulo} (${selected}/${total})`,
          defaultExpanded: true,
          moduleCheckbox: {
            checked: anyLeafChecked,
            disabled: disabled || loading,
            checkboxOpacity: total === 0 ? 1 : allSelectedInMod ? 1 : 0.6,
            onChange: next => applyModuleAllows(tasksInMod, next),
          },
          rows: tasksInMod.map(task => {
            const row = localByTaskId[task.id];
            const checked = row ? isAllowTrue(row.allow) : false;
            const wasInitiallyAllowed = initialAllowByTaskId[task.id] ?? false;
            const labelBold = checked && !wasInitiallyAllowed;
            const labelItalic = !checked && wasInitiallyAllowed;

            return {
              key: String(task.id),
              label: task.task ?? `(id ${task.id})`,
              checked,
              labelBold,
              labelItalic,
              disabled: disabled || loading,
              onCheckedChange: (nextChecked: boolean) => applyLocalAllow(task, nextChecked),
            };
          }),
        };
      });
    }, [catalog, initialAllowByTaskId, localByTaskId, disabled, loading, applyLocalAllow, applyModuleAllows]);

    return (
      <CommonsTree
        legend="Permisos por módulo y tarea (`allow`)"
        branches={branches}
        loading={loading}
        error={fetchError}
        accentColor={accentColor}
      />
    );
  },
);

export default PerfilSistemaTasksPanel;
